import { PlusIcon } from '@acceso/design-system';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { usePostFeaturedPeople } from '@/features/featured-people/api/post-featured-people';
import { TFeaturedPeopleForm, TFeaturedPeopleType } from '@/features/featured-people/types';
import { usePatchSegmentPeople } from '@/features/segments/api/patch-segment-people';
import { usePostSegmentPeople } from '@/features/segments/api/post-segment-people';
import { TPostSegmentPeople } from '@/features/segments/types';
import { useDisclosureWithData } from '@/hooks/useDisclosureWithData';
import { convertISOToSeconds } from '@/utils/hh-mm-ss-into-number';

import { PEOPLE_FORM_DEFAULT_VALUES } from '../constants';
import { TBroadcastScheduleProgramBodySegmentToolkitPeopleForm } from '../types';

export function useBroadcastScheduleProgramBodySegmentToolkitPeopleFormState() {
  const {
    personaData: peopleData,
    drawerRect,
    activeSegment,
    formBox,
    playerVideoCurrentTime,
    getDateTimeCurrentSegment,
    setPersonaData,
    setFormBox,
    setActiveSegment,
    setVisibleBox,
    drawnFormReset,
    setSegmentItems,
  } = useBroadcastScheduleProgram();
  const postSegmentPeople = usePostSegmentPeople();
  const patchSegmentPeople = usePatchSegmentPeople();
  const form = useForm<TBroadcastScheduleProgramBodySegmentToolkitPeopleForm>({
    mode: 'all',
    defaultValues: PEOPLE_FORM_DEFAULT_VALUES,
  });
  const peopleDrawer = useDisclosureWithData();
  const { t } = useTranslation();
  const { mutateAsync: createPeople, isPending: isCreatingPeople } = usePostFeaturedPeople();

  const renderAdditionalButtons = () => {
    return [
      {
        label: t('Crear nueva persona de interés'),
        icon: PlusIcon,
        onClick: () => peopleDrawer.open(),
      },
    ];
  };

  const handleSubmitPeople = useCallback(
    async (data: TFeaturedPeopleForm): Promise<TFeaturedPeopleType> => {
      try {
        const result = await createPeople(data);
        peopleDrawer.close();
        return result;
      } catch (error) {
        console.error('Error during mutation', error);
        throw error;
      }
    },
    [createPeople]
  );

  const handleSubmit = async (data: TBroadcastScheduleProgramBodySegmentToolkitPeopleForm) => {
    const start_datetime =
      data.start_datetime ?? getDateTimeCurrentSegment(playerVideoCurrentTime) ?? null;
    const formatedData: TPostSegmentPeople = {
      ...data,
      start_datetime,
      end_datetime: data.end_datetime ?? start_datetime,
      id: data.people_id,
      parameters: {
        ...data.parameters,
        bbox: data.parameters.bbox || [0.045, 0.075, 0.96, 0.925], // Full screen
      },
      segment_id: activeSegment?.id || '',
      entity_score: Number(data.entity_score) / 100,
    };
    if (peopleData?.id) {
      handleUpdate(formatedData);
    } else {
      handleCreate(formatedData);
    }
  };

  const handleCreate = async (formatedData: TPostSegmentPeople) => {
    try {
      const item = await postSegmentPeople.mutateAsync(formatedData);
      setActiveSegment(prev => {
        if (prev) {
          return {
            ...prev,
            segment_peoples: [...(prev.segment_peoples || []), item],
          };
        }
        return prev;
      });
      setSegmentItems(prev =>
        prev.map(segment => {
          if (segment.id === formatedData?.segment_id) {
            return {
              ...segment,
              segment_peoples: [...(segment.segment_peoples || []), item],
            };
          }
          return segment;
        })
      );
      drawnFormReset();
    } catch (error) {
      console.error('Error al crear el people:', error);
    }
  };

  const handleUpdate = async (formatedData: TPostSegmentPeople) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { segment_id, ...restData } = formatedData;
    try {
      const item = await patchSegmentPeople.mutateAsync({
        id: peopleData?.id || '',
        body: restData,
      });
      setActiveSegment(prev => {
        if (prev) {
          return {
            ...prev,
            segment_peoples: prev.segment_peoples?.map(people => {
              if (people.id === item.id) {
                return item;
              }
              return people;
            }),
          };
        }
        return prev;
      });
      setSegmentItems(prev =>
        prev.map(segment => {
          if (segment.id === formatedData?.segment_id) {
            return {
              ...segment,
              segment_peoples: (segment.segment_peoples || []).map(ocr => {
                if (ocr.id === item.id) {
                  return item;
                }
                return ocr;
              }),
            };
          }
          return segment;
        })
      );
      drawnFormReset();
    } catch (error) {
      console.error('Error al actualizar la persona de interés:', error);
    }
  };

  const handleClickExit = useCallback(() => {
    form.reset(PEOPLE_FORM_DEFAULT_VALUES);
    setPersonaData(null);
    drawnFormReset();
  }, [setFormBox, setPersonaData]);

  useEffect(() => {
    if (drawerRect && formBox === 'people') {
      form.setValue('parameters.bbox', drawerRect);
    }
  }, [drawerRect]);

  useEffect(() => {
    if (formBox !== 'people') return;

    if (peopleData?.id) {
      form.setValue('people_id', peopleData.people_id);
      form.setValue('parameters.bbox', peopleData.parameters.bbox);
      form.setValue('start_datetime', new Date(peopleData.start_datetime));
      form.setValue('end_datetime', new Date(peopleData.end_datetime || peopleData.start_datetime));
      form.setValue('entity_score', peopleData.entity_score * 100);
      form.setValue('review_state', peopleData.review_state);
      form.setValue('parameters.visibility', peopleData.parameters.visibility);

      if (!activeSegment || !peopleData.start_datetime) return;

      const seconds = convertISOToSeconds(
        peopleData.start_datetime,
        new Date(activeSegment.start_datetime)
      );

      setVisibleBox({
        bbox: peopleData?.parameters.bbox || [0, 0, 0, 0],
        frame: seconds,
        entity_label: peopleData?.entity_label || '',
      });
    }
  }, [peopleData]);

  return {
    form,
    isCreatingPeople,
    peopleDrawer,
    renderAdditionalButtons,
    handleSubmitPeople,
    handleSubmit,
    handleClickExit,
  };
}
